import {
  doc,
  collection,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore'
import { db } from '@/firebase'
import { canEditStudent } from '@/config/roles'
import { RULE_EXPECTATIONS, attempt, accessDetail, probe } from '../ruleExpectations.js'
import { buildEvaluationRecord, emptyEvalDates } from '@/utils/studentEvaluation.js'

// DESTRUCTIVE: New Evaluation fillable-shell shape + CF mirror (consent-gated).
//
// Creates a student the way createMinimalStudent now does (plan + initial,
// empty plan CM), opens an eval assigned to the current user (referral only),
// and asserts the referral window first: staffIds access without a public
// mirror. Then signs AP consent and waits for app.evalCaseManagerId. Both
// phases assert the plan CM stayed empty — the shell does not park the eval
// CM in caseManagerId.
//
// Eval-CM-only *edit* (plain CM, empty plan CM) cannot be proven in this
// runner — create-roles already edit IEP students by plan, and a CM cannot
// create the student/eval. That clause is covered by
// tests/firebase-rules.test.js "students: eval CM can edit a fillable shell".
//
// Manual confirm after deploy (hosted):
//   1. Chair: New Evaluation → student not in the app → assign a CM.
//      Roster shows CM: — and NO yellow Initial tile until AP consent is
//      entered on the eval record; the tile appears once consent is saved.
//   2. Sign in as that CM. Open the student, add a period, save. Should work
//      after the mirror lands (~seconds). Identity/plan fields stay read-only.
//   3. Most eval shells will not be in an Aeries schedule file — typed
//      periods stay, eval card still says entered manually. If that SSID
//      is in the file, periods update and the card says from Aeries; plan /
//      planStatus / empty caseManagerId must not change.
//   4. SEIS Pending on a brand-new SSID: same shell (empty plan CM + IEP eval).
//   5. Same SSID already a 504: do not create a shell — attach the eval (Path A).

const POLL_INTERVAL_MS = 2500
const POLL_TIMEOUT_MS = 75000

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function pollStudent(ref, check) {
  const deadline = Date.now() + POLL_TIMEOUT_MS
  let last = 'no read yet'
  while (Date.now() < deadline) {
    let state
    try {
      const snap = await getDoc(ref)
      state = { denied: false, exists: snap.exists(), data: snap.exists() ? snap.data() : null }
    } catch (err) {
      if (err?.code !== 'permission-denied') throw err
      state = { denied: true, exists: null, data: null }
    }
    const result = check(state)
    if (result) return result
    last = state.denied
      ? 'read denied'
      : `evalCaseManagerId=${JSON.stringify(state.data?.app?.evalCaseManagerId ?? null)}`
    await sleep(POLL_INTERVAL_MS)
  }
  throw new Error(
    `Timed out after ${POLL_TIMEOUT_MS / 1000}s waiting for Cloud Function (last: ${last}). ` +
      'Are functions deployed to this project?'
  )
}

export default {
  id: 'evalFillableShell',
  label: 'Fillable eval shell (empty plan CM)',
  description:
    'New-eval shell: plan + initial, empty plan CM. Waits for the CF mirror and asserts caseManagerId stays empty. Needs deployed functions. Eval-CM edit is in the emulator rules tests — see scenario comment for a hosted manual checklist.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const uid = ctx.user.uid

    if (!ctx.schoolId) {
      step.block('setup', 'Select a school before running the fillable-shell smoke.')
      return
    }
    if (
      !RULE_EXPECTATIONS.studentCreate(role) ||
      !RULE_EXPECTATIONS.studentEvaluationCreate(role)
    ) {
      step.block(
        'setup',
        `Role ${role} cannot create students/evaluations — use a sped chair, psych, or admin. A CM’s edit of this shell is covered by the emulator rules tests.`
      )
      return
    }

    const studentRef = doc(collection(db, 'students'))
    const evalRef = doc(collection(db, 'studentEvaluations'))

    const studentDoc = {
      app: {
        studentData: {
          firstName: 'SMOKE',
          lastName: `EvalShell ${ctx.runId.slice(-8)}`,
          plan: 'IEP',
          planStatus: 'initial',
          grade: '7',
          ssid: `SMOKE-EVALSHELL-${ctx.runId}`,
          caseManagerId: ''
        },
        accommodations: { instruction: '', assessment: '' },
        schedule: { classServices: [] },
        staffIds: []
      },
      __smokeTest: true,
      __smokeRunId: ctx.runId,
      createdAt: serverTimestamp(),
      schoolId: ctx.schoolId
    }

    try {
      await step('create fillable shell (empty plan CM)', async () => {
        const { allowed } = await attempt(() => setDoc(studentRef, studentDoc))
        if (!allowed) throw new Error('Could not create the shell student')
        const snap = await getDoc(studentRef)
        const cm = snap.data()?.app?.studentData?.caseManagerId ?? 'MISSING'
        if (cm !== '') {
          throw new Error(`SECURITY: shell plan caseManagerId was ${JSON.stringify(cm)}`)
        }
        return accessDetail(true, true) + ` — students/${studentRef.id}`
      })

      await step('create IEP eval assigned to you', async () => {
        const record = buildEvaluationRecord({
          studentId: studentRef.id,
          schoolId: ctx.schoolId,
          evalType: 'IEP',
          caseManagerId: uid,
          createdBy: uid,
          dates: { ...emptyEvalDates(), referral: '2026-08-01' },
          source: 'manual'
        })
        await setDoc(evalRef, {
          ...record,
          __smokeTest: true,
          __smokeRunId: ctx.runId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
        return `studentEvaluations/${evalRef.id}`
      })

      await step('referral window: staffIds set, NO public mirror, plan CM empty', async () => {
        return pollStudent(studentRef, ({ denied, data }) => {
          if (denied || !data) return null
          const planCm = data.app?.studentData?.caseManagerId ?? 'MISSING'
          if (planCm !== '') {
            throw new Error(`SECURITY: CF parked eval CM in plan caseManagerId (${planCm})`)
          }
          const plan = data.app?.studentData?.plan
          const status = data.app?.studentData?.planStatus
          if (plan !== 'IEP' || status !== 'initial') {
            throw new Error(`SECURITY: shell plan fields changed to ${plan}/${status}`)
          }
          const staffIds = data.app?.staffIds || []
          if (!staffIds.includes(uid)) return null
          const mirror = data.app?.evalCaseManagerId || ''
          if (mirror) {
            throw new Error(
              `FERPA: evalCaseManagerId (${mirror}) written before AP consent — ` +
                'the consent gate is broken'
            )
          }
          return 'staffIds set, mirror absent (consent pending); plan CM still empty'
        })
      })

      await step('sign AP consent → CF mirrors eval CM; plan CM stays empty', async () => {
        await updateDoc(evalRef, {
          dates: { ...emptyEvalDates(), referral: '2026-08-01', consent: '2026-08-15' },
          updatedAt: serverTimestamp()
        })
        return pollStudent(studentRef, ({ denied, data }) => {
          if (denied || !data) return null
          const planCm = data.app?.studentData?.caseManagerId ?? 'MISSING'
          if (planCm !== '') {
            throw new Error(`SECURITY: CF parked eval CM in plan caseManagerId (${planCm})`)
          }
          const mirror = data.app?.evalCaseManagerId || ''
          const staffIds = data.app?.staffIds || []
          if (mirror === uid && staffIds.includes(uid)) {
            return 'evalCaseManagerId + staffIds set after consent; plan CM still empty'
          }
          return null
        })
      })

      // Expectation from the client mirror: chairs/admins edit the shell
      // (plan IEP); school_psych creates students but cannot edit them.
      const shellShape = {
        app: {
          studentData: { plan: 'IEP', planStatus: 'initial', caseManagerId: '' },
          evalCaseManagerId: uid
        }
      }
      const clientSaysEdit = canEditStudent(shellShape, { uid, role })
      await step(
        `save a schedule period on the shell (client says: ${clientSaysEdit ? 'editable' : 'not editable'})`,
        () =>
          probe(clientSaysEdit, () =>
            updateDoc(studentRef, {
              'app.schedule.periods': { 1: 'smoke-teacher' },
              updatedAt: serverTimestamp()
            })
          )
      )
      if (clientSaysEdit) {
        await step('schedule save left plan CM empty', async () => {
          const snap = await getDoc(studentRef)
          const planCm = snap.data()?.app?.studentData?.caseManagerId ?? 'MISSING'
          if (planCm !== '') {
            throw new Error(`SECURITY: schedule save wrote a plan CM (${planCm})`)
          }
          return 'period saved; caseManagerId still empty'
        })
      }
    } finally {
      await deleteDoc(evalRef).catch(() => {})
      await deleteDoc(studentRef).catch(() => {})
    }
  }
}
